/**
 * Cached, fail-open pre-send gate for AI token limits.
 *
 * The Functions proxy remains the enforcement authority. This gate only keeps
 * the UI from starting a request that a recent, complete usage summary says
 * will be rejected. Checks happen at request start; callers must not use a
 * later refresh to interrupt an in-flight response.
 */

export const AI_USAGE_GATE_CACHE_TTL_MS = 60_000;

export type AIUsageGateScope =
  | { readonly kind: "league"; readonly leagueID: string }
  | { readonly kind: "personal"; readonly userID: string };

/** The subset of the summary contract needed by the gate. */
export interface AIUsageGateSummary {
  readonly mode: "league" | "personal";
  readonly leagueID?: string | undefined;
  readonly limit: {
    readonly period: "hour" | "day" | "week" | "month" | "year";
  } | null;
  readonly nextResetAt: Date | string;
  readonly isOverLimit: boolean;
  readonly truncated: boolean;
}

export interface AIUsageGateSummaryRequest {
  readonly leagueID?: string | undefined;
  readonly includeBreakdown: false;
}

export type AIUsageGateSummaryProvider<Summary extends AIUsageGateSummary = AIUsageGateSummary> =
  (request: AIUsageGateSummaryRequest) => Promise<Summary>;

export interface AIUsageGateState<Summary extends AIUsageGateSummary = AIUsageGateSummary> {
  readonly summary: Summary | null;
  readonly blockMessage: string | null;
}

interface CachedGateState<Summary extends AIUsageGateSummary> extends AIUsageGateState<Summary> {
  readonly fetchedAtMs: number;
}

export interface AIUsageGateOptions {
  readonly cacheTTLMs?: number;
  readonly now?: () => number;
}

export class AIUsageGate<Summary extends AIUsageGateSummary = AIUsageGateSummary> {
  private readonly cache = new Map<string, CachedGateState<Summary>>();
  private readonly cacheTTLMs: number;
  private readonly now: () => number;

  constructor(
    private readonly provider: AIUsageGateSummaryProvider<Summary>,
    options: AIUsageGateOptions = {},
  ) {
    this.cacheTTLMs = options.cacheTTLMs ?? AI_USAGE_GATE_CACHE_TTL_MS;
    this.now = options.now ?? Date.now;
  }

  /** Returns the latest state for one scope without performing IO. */
  state(scope: AIUsageGateScope): AIUsageGateState<Summary> {
    const cached = this.cache.get(scopeKey(scope));
    return cached ? publicState(cached) : { summary: null, blockMessage: null };
  }

  summary(scope: AIUsageGateScope): Summary | null {
    return this.state(scope).summary;
  }

  blockMessage(scope: AIUsageGateScope): string | null {
    return this.state(scope).blockMessage;
  }

  isStale(scope: AIUsageGateScope): boolean {
    const cached = this.cache.get(scopeKey(scope));
    return cached === undefined || this.now() - cached.fetchedAtMs >= this.cacheTTLMs;
  }

  /**
   * Refreshes when forced or stale. Fetch failures are cached as an open
   * decision so an unavailable meter cannot break chat or cause a fetch loop.
   */
  async refresh(
    scope: AIUsageGateScope,
    options: { readonly force?: boolean } = {},
  ): Promise<AIUsageGateState<Summary>> {
    if (!options.force && !this.isStale(scope)) {
      return this.state(scope);
    }

    const key = scopeKey(scope);
    try {
      const summary = await this.provider({
        ...(scope.kind === "league" ? { leagueID: scope.leagueID } : {}),
        includeBreakdown: false,
      });
      const next: CachedGateState<Summary> = {
        fetchedAtMs: this.now(),
        summary,
        blockMessage: aiUsageBlockMessage(summary),
      };
      this.cache.set(key, next);
      return publicState(next);
    } catch {
      const next: CachedGateState<Summary> = {
        fetchedAtMs: this.now(),
        summary: null,
        blockMessage: null,
      };
      this.cache.set(key, next);
      return publicState(next);
    }
  }

  /** Refreshes only if stale, then returns nil when the request may proceed. */
  async blockReasonRefreshingIfStale(scope: AIUsageGateScope): Promise<string | null> {
    return (await this.refresh(scope)).blockMessage;
  }

  /** Invalidates one scope, or every cached scope when omitted. */
  invalidate(scope?: AIUsageGateScope): void {
    if (scope) {
      this.cache.delete(scopeKey(scope));
    } else {
      this.cache.clear();
    }
  }
}

export function aiUsageScopeKey(scope: AIUsageGateScope): string {
  return scopeKey(scope);
}

/** A truncated scan can undercount, so only a complete over-limit result blocks. */
export function aiUsageBlockMessage(summary: AIUsageGateSummary): string | null {
  if (!summary.isOverLimit || summary.truncated || summary.limit === null) return null;

  const owner = summary.mode === "league" ? "This league's" : "Your";
  const reset = resetInstant(summary.nextResetAt);
  const resetClause = reset ? ` It resets on ${reset}.` : "";
  return `${owner} AI token limit for the ${summary.limit.period} has been reached.${resetClause}`;
}

function scopeKey(scope: AIUsageGateScope): string {
  return scope.kind === "league" ? `league:${scope.leagueID}` : `user:${scope.userID}`;
}

function publicState<Summary extends AIUsageGateSummary>(
  cached: CachedGateState<Summary>,
): AIUsageGateState<Summary> {
  return { summary: cached.summary, blockMessage: cached.blockMessage };
}

function resetInstant(value: Date | string): string | null {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return null;

  const day = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
  return `${day} at ${time} UTC`;
}
